import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { formatEther, parseGwei } from "viem";
import {
  useAccount,
  useReadContract,
  useWaitForTransactionReceipt,
  useWriteContract,
} from "wagmi";

import TradePanel from "../components/TradePanel";
import { factoryAbi } from "../contracts/factoryAbi";
import { factoryAddress } from "../contracts/factoryAddress";

type NormalizedMarket = {
  id: bigint;
  question: string;
  resolutionSource: string;
  deadline: bigint;
  creator: string;
  resolved: boolean;
  outcome: boolean;
  yesPool: bigint;
  noPool: bigint;
  totalYesShares: bigint;
  totalNoShares: bigint;
  virtualLiquidity: bigint;
  resolutionEvidenceUrl: string;
  resolutionNotes: string;
  resolver: string;
  resolvedAt: bigint;
};

type NormalizedPosition = {
  yesShares: bigint;
  noShares: bigint;
  alreadyClaimed: boolean;
};

type PriceHistoryPoint = {
  ts: number;
  yesPriceBps: number;
  noPriceBps: number;
};

const HISTORY_LIMIT = 60;
const HISTORY_MIN_INTERVAL_MS = 15_000;
const AMOY_MAX_PRIORITY_FEE_PER_GAS = parseGwei("30");
const AMOY_MAX_FEE_PER_GAS = parseGwei("60");

function formatPol(value: bigint, decimals = 4) {
  try {
    const num = Number(formatEther(value));
    if (!Number.isFinite(num)) return "0";
    return num.toFixed(decimals);
  } catch {
    return "0";
  }
}

function formatDateFromUnix(timestamp: bigint) {
  try {
    const ms = Number(timestamp) * 1000;
    if (!Number.isFinite(ms) || ms <= 0) return "—";
    return new Date(ms).toLocaleString();
  } catch {
    return "—";
  }
}

function formatPercentFromBps(value: number | bigint) {
  return `${(Number(value) / 100).toFixed(1)}%`;
}

function shortAddress(value: string, left = 6, right = 4) {
  if (!value) return "—";
  if (value.length <= left + right) return value;
  return `${value.slice(0, left)}...${value.slice(-right)}`;
}

function safeBigInt(value: unknown): bigint {
  if (typeof value === "bigint") return value;
  if (typeof value === "number") return BigInt(value);
  if (typeof value === "string") {
    try {
      return BigInt(value);
    } catch {
      return 0n;
    }
  }
  return 0n;
}

function safeBoolean(value: unknown): boolean {
  return Boolean(value);
}

function safeString(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function normalizeMarket(raw: unknown): NormalizedMarket {
  if (Array.isArray(raw)) {
    return {
      id: safeBigInt(raw[0]),
      question: safeString(raw[1]),
      resolutionSource: safeString(raw[2]),
      deadline: safeBigInt(raw[3]),
      creator: safeString(raw[4]),
      resolved: safeBoolean(raw[5]),
      outcome: safeBoolean(raw[6]),
      yesPool: safeBigInt(raw[7]),
      noPool: safeBigInt(raw[8]),
      totalYesShares: safeBigInt(raw[9]),
      totalNoShares: safeBigInt(raw[10]),
      virtualLiquidity: safeBigInt(raw[11]),
      resolutionEvidenceUrl: safeString(raw[12]),
      resolutionNotes: safeString(raw[13]),
      resolver: safeString(raw[14]),
      resolvedAt: safeBigInt(raw[15]),
    };
  }

  if (raw && typeof raw === "object") {
    const obj = raw as Record<string, unknown>;
    return {
      id: safeBigInt(obj.id),
      question: safeString(obj.question),
      resolutionSource: safeString(obj.resolutionSource),
      deadline: safeBigInt(obj.deadline),
      creator: safeString(obj.creator),
      resolved: safeBoolean(obj.resolved),
      outcome: safeBoolean(obj.outcome),
      yesPool: safeBigInt(obj.yesPool),
      noPool: safeBigInt(obj.noPool),
      totalYesShares: safeBigInt(obj.totalYesShares),
      totalNoShares: safeBigInt(obj.totalNoShares),
      virtualLiquidity: safeBigInt(obj.virtualLiquidity),
      resolutionEvidenceUrl: safeString(obj.resolutionEvidenceUrl),
      resolutionNotes: safeString(obj.resolutionNotes),
      resolver: safeString(obj.resolver),
      resolvedAt: safeBigInt(obj.resolvedAt),
    };
  }

  return {
    id: 0n,
    question: "",
    resolutionSource: "",
    deadline: 0n,
    creator: "",
    resolved: false,
    outcome: false,
    yesPool: 0n,
    noPool: 0n,
    totalYesShares: 0n,
    totalNoShares: 0n,
    virtualLiquidity: 0n,
    resolutionEvidenceUrl: "",
    resolutionNotes: "",
    resolver: "",
    resolvedAt: 0n,
  };
}

function normalizePosition(raw: unknown): NormalizedPosition {
  if (Array.isArray(raw)) {
    return {
      yesShares: safeBigInt(raw[0]),
      noShares: safeBigInt(raw[1]),
      alreadyClaimed: safeBoolean(raw[2]),
    };
  }

  if (raw && typeof raw === "object") {
    const obj = raw as Record<string, unknown>;
    return {
      yesShares: safeBigInt(obj.yesAmount ?? obj.yesShares),
      noShares: safeBigInt(obj.noAmount ?? obj.noShares),
      alreadyClaimed: safeBoolean(obj.alreadyClaimed),
    };
  }

  return {
    yesShares: 0n,
    noShares: 0n,
    alreadyClaimed: false,
  };
}

function calculateResolvedPayout(
  userWinnerShares: bigint,
  totalWinnerShares: bigint,
  totalPool: bigint
) {
  if (userWinnerShares <= 0n || totalWinnerShares <= 0n || totalPool <= 0n) {
    return 0n;
  }
  return (userWinnerShares * totalPool) / totalWinnerShares;
}

function formatCountdown(deadline: bigint, nowTick: number) {
  const end = Number(deadline) * 1000;
  const diff = end - nowTick;
  if (diff <= 0) return "Closed";

  const totalSeconds = Math.floor(diff / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (days > 0) return `${days}d ${hours}h left`;
  if (hours > 0) return `${hours}h ${minutes}m left`;
  if (minutes > 0) return `${minutes}m ${seconds}s left`;
  return `${seconds}s left`;
}

function getHistoryStorageKey(marketId: string) {
  return `orion-market-history-${marketId}`;
}

function loadHistory(marketId: string): PriceHistoryPoint[] {
  if (typeof window === "undefined" || !marketId) return [];
  try {
    const raw = window.localStorage.getItem(getHistoryStorageKey(marketId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as PriceHistoryPoint[];
    if (!Array.isArray(parsed)) return [];
    return parsed.slice(-HISTORY_LIMIT);
  } catch {
    return [];
  }
}

function saveHistory(marketId: string, history: PriceHistoryPoint[]) {
  if (typeof window === "undefined" || !marketId) return;
  try {
    window.localStorage.setItem(
      getHistoryStorageKey(marketId),
      JSON.stringify(history.slice(-HISTORY_LIMIT))
    );
  } catch {}
}

function formatTimeLabel(ts: number) {
  try {
    return new Date(ts).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function buildSvgPath(points: { x: number; y: number }[]) {
  if (!points.length) return "";
  return points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`)
    .join(" ");
}

function ProbabilityChart({ history }: { history: PriceHistoryPoint[] }) {
  const width = 760;
  const height = 220;
  const padding = 20;

  const chartPoints = useMemo(() => {
    if (history.length === 0) return [];
    if (history.length === 1) {
      return [
        {
          x: width / 2,
          y:
            padding +
            ((10000 - history[0].yesPriceBps) / 10000) *
              (height - padding * 2),
        },
      ];
    }

    return history.map((point, index) => ({
      x: padding + (index / (history.length - 1)) * (width - padding * 2),
      y:
        padding +
        ((10000 - point.yesPriceBps) / 10000) * (height - padding * 2),
    }));
  }, [history]);

  const path = useMemo(() => buildSvgPath(chartPoints), [chartPoints]);
  const latest = history[history.length - 1];
  const earliest = history[0];
  const delta = latest && earliest ? latest.yesPriceBps - earliest.yesPriceBps : 0;

  return (
    <div className="rounded-2xl border bg-card p-6 shadow-sm">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold">Probability chart</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            YES probability movement during this session.
          </p>
        </div>
        <div className="text-right">
          <div className="text-sm text-muted-foreground">Session move</div>
          <div
            className={`mt-1 text-sm font-semibold ${
              delta > 0
                ? "text-emerald-600"
                : delta < 0
                ? "text-rose-600"
                : "text-muted-foreground"
            }`}
          >
            {delta > 0 ? "+" : ""}
            {(delta / 100).toFixed(1)} pts
          </div>
        </div>
      </div>

      {history.length === 0 ? (
        <div className="rounded-xl border bg-background p-6 text-sm text-muted-foreground">
          No price history yet. Open this market and make a trade to start building the chart.
        </div>
      ) : (
        <>
          <div className="mb-3 flex items-center justify-between text-xs text-muted-foreground">
            <span>100%</span>
            <span>YES probability</span>
            <span>0%</span>
          </div>

          <div className="overflow-hidden rounded-2xl border bg-background">
            <svg
              viewBox={`0 0 ${width} ${height}`}
              className="h-[220px] w-full"
              preserveAspectRatio="none"
            >
              {[0, 25, 50, 75, 100].map((level) => {
                const y = padding + ((100 - level) / 100) * (height - padding * 2);
                return (
                  <g key={level}>
                    <line
                      x1={padding}
                      y1={y}
                      x2={width - padding}
                      y2={y}
                      stroke="currentColor"
                      strokeOpacity="0.08"
                    />
                    <text
                      x={8}
                      y={y + 4}
                      fontSize="10"
                      fill="currentColor"
                      opacity="0.45"
                    >
                      {level}%
                    </text>
                  </g>
                );
              })}

              {chartPoints.length > 0 && (
                <>
                  <path
                    d={path}
                    fill="none"
                    stroke="currentColor"
                    strokeOpacity="0.9"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  {chartPoints.map((point, index) => {
                    const isLast = index === chartPoints.length - 1;
                    return (
                      <circle
                        key={`${point.x}-${point.y}-${index}`}
                        cx={point.x}
                        cy={point.y}
                        r={isLast ? 4 : 2.5}
                        fill="currentColor"
                        fillOpacity={isLast ? 0.95 : 0.45}
                      />
                    );
                  })}
                </>
              )}
            </svg>
          </div>

          <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
            <span>{history[0] ? formatTimeLabel(history[0].ts) : "Start"}</span>
            <span>
              Latest YES: {latest ? (latest.yesPriceBps / 100).toFixed(1) : "0.0"}%
            </span>
            <span>
              {history[history.length - 1]
                ? formatTimeLabel(history[history.length - 1].ts)
                : "Now"}
            </span>
          </div>
        </>
      )}
    </div>
  );
}

export default function MarketDetailPage() {
  const params = useParams();
  const marketId = params.marketId ?? params.id ?? "";

  const parsedMarketId = useMemo(() => {
    try {
      return BigInt(marketId);
    } catch {
      return 0n;
    }
  }, [marketId]);

  const contractAddress = factoryAddress as `0x${string}`;
  const { address, isConnected } = useAccount();

  const [nowTick, setNowTick] = useState(Date.now());
  const [history, setHistory] = useState<PriceHistoryPoint[]>([]);

  useEffect(() => {
    const interval = window.setInterval(() => setNowTick(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    if (marketId) setHistory(loadHistory(marketId));
  }, [marketId]);

  const {
    data: marketRaw,
    isLoading,
    isError,
    refetch: refetchMarket,
  } = useReadContract({
    address: contractAddress,
    abi: factoryAbi,
    functionName: "getMarket",
    args: [parsedMarketId],
    query: { enabled: !!marketId, refetchInterval: 15_000 },
  });

  const { data: pricesRaw, refetch: refetchPrices } = useReadContract({
    address: contractAddress,
    abi: factoryAbi,
    functionName: "getPrices",
    args: [parsedMarketId],
    query: { enabled: !!marketId, refetchInterval: 15_000 },
  });

  const { data: userPositionRaw, refetch: refetchUserPosition } = useReadContract({
    address: contractAddress,
    abi: factoryAbi,
    functionName: "getUserPosition",
    args: address ? [parsedMarketId, address] : undefined,
    query: {
      enabled: !!marketId && !!address && isConnected,
      refetchInterval: 15_000,
    },
  });

  const {
    writeContract,
    data: claimHash,
    isPending: isClaimPending,
    error: claimError,
  } = useWriteContract();

  const { isLoading: isClaimConfirming, isSuccess: isClaimConfirmed } =
    useWaitForTransactionReceipt({
      hash: claimHash,
      query: { enabled: !!claimHash },
    });

  const market = useMemo(() => normalizeMarket(marketRaw), [marketRaw]);
  const userPosition = useMemo(() => normalizePosition(userPositionRaw), [userPositionRaw]);

  const yesPriceBps = useMemo(() => {
    if (Array.isArray(pricesRaw) && pricesRaw[0] != null) return Number(pricesRaw[0]);
    return 5000;
  }, [pricesRaw]);

  const noPriceBps = useMemo(() => {
    if (Array.isArray(pricesRaw) && pricesRaw[1] != null) return Number(pricesRaw[1]);
    return 5000;
  }, [pricesRaw]);

  useEffect(() => {
    if (!marketId || !pricesRaw) return;
    const nextPoint: PriceHistoryPoint = { ts: Date.now(), yesPriceBps, noPriceBps };

    setHistory((prev) => {
      const current = prev.length ? prev : loadHistory(marketId);
      const last = current[current.length - 1];
      const shouldSkip =
        last &&
        last.yesPriceBps === nextPoint.yesPriceBps &&
        nextPoint.ts - last.ts < HISTORY_MIN_INTERVAL_MS;

      if (shouldSkip) return current;

      const nextHistory = [...current, nextPoint].slice(-HISTORY_LIMIT);
      saveHistory(marketId, nextHistory);
      return nextHistory;
    });
  }, [marketId, pricesRaw, yesPriceBps, noPriceBps]);

  useEffect(() => {
    if (!isClaimConfirmed) return;
    Promise.all([refetchMarket(), refetchPrices(), refetchUserPosition()]).catch(
      console.error
    );
  }, [isClaimConfirmed, refetchMarket, refetchPrices, refetchUserPosition]);

  const totalPool = market.yesPool + market.noPool;

  const estimatedIfYesWins = calculateResolvedPayout(
    userPosition.yesShares,
    market.totalYesShares,
    totalPool
  );

  const estimatedIfNoWins = calculateResolvedPayout(
    userPosition.noShares,
    market.totalNoShares,
    totalPool
  );

  const claimablePayout =
    market.resolved && !userPosition.alreadyClaimed
      ? market.outcome
        ? estimatedIfYesWins
        : estimatedIfNoWins
      : 0n;

  const canClaim =
    isConnected &&
    market.resolved &&
    claimablePayout > 0n &&
    !userPosition.alreadyClaimed &&
    !isClaimPending &&
    !isClaimConfirming;

  const isClosedByTime = Number(market.deadline) * 1000 <= nowTick;
  const marketStatusLabel = market.resolved ? "Resolved" : isClosedByTime ? "Closed" : "Open";

  function handleClaim() {
    if (!canClaim) return;
    writeContract({
      address: contractAddress,
      abi: factoryAbi,
      functionName: "claimWinnings",
      args: [parsedMarketId],
      maxPriorityFeePerGas: AMOY_MAX_PRIORITY_FEE_PER_GAS,
      maxFeePerGas: AMOY_MAX_FEE_PER_GAS,
    } as any);
  }

  const claimStatusText = useMemo(() => {
    if (isClaimPending) return "Please confirm the claim in your wallet.";
    if (isClaimConfirming)
      return "Claim transaction submitted. Waiting for confirmation...";
    if (isClaimConfirmed) return "Claim successful. Market data refreshed.";
    if (claimError) return claimError.message || "Claim failed.";
    return "";
  }, [isClaimPending, isClaimConfirming, isClaimConfirmed, claimError]);

  if (!marketId) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-10">
        <div className="rounded-2xl border bg-card p-6 shadow-sm">
          <h1 className="text-xl font-semibold">Market not found</h1>
          <p className="mt-2 text-muted-foreground">
            No market ID was provided in the URL.
          </p>
          <Link
            to="/"
            className="mt-4 inline-flex rounded-xl border px-4 py-2 text-sm font-medium"
          >
            Back to markets
          </Link>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-10">
        <div className="rounded-2xl border bg-card p-6 shadow-sm">
          <h1 className="text-xl font-semibold">Loading market...</h1>
          <p className="mt-2 text-muted-foreground">
            Fetching on-chain market data from OrionMarketFactory.
          </p>
        </div>
      </div>
    );
  }

  if (isError || !marketRaw) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-10">
        <div className="rounded-2xl border bg-card p-6 shadow-sm">
          <h1 className="text-xl font-semibold">Failed to load market</h1>
          <p className="mt-2 text-muted-foreground">
            We could not read this market from the factory contract.
          </p>
          <div className="mt-4 flex gap-3">
            <button
              type="button"
              onClick={() => refetchMarket()}
              className="rounded-xl bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
            >
              Try again
            </button>
            <Link to="/" className="rounded-xl border px-4 py-2 text-sm font-medium">
              Back to markets
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-8">
      <div className="mb-6 flex items-center justify-between gap-4">
        <div>
          <Link to="/" className="text-sm text-muted-foreground underline">
            ← Back to markets
          </Link>
          <h1 className="mt-2 text-2xl font-bold tracking-tight">
            {market.question || `Market #${marketId}`}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            On-chain market ID: {market.id.toString()}
          </p>
        </div>
      </div>

      <div className="mb-6 grid gap-4 sm:grid-cols-3">
        <div className="rounded-xl border bg-card p-4 shadow-sm">
          <div className="text-sm text-muted-foreground">Status</div>
          <div className="mt-1 text-xl font-semibold">{marketStatusLabel}</div>
        </div>
        <div className="rounded-xl border bg-card p-4 shadow-sm">
          <div className="text-sm text-muted-foreground">Closes in</div>
          <div className="mt-1 text-xl font-semibold">
            {market.resolved ? "Resolved" : formatCountdown(market.deadline, nowTick)}
          </div>
        </div>
        <div className="rounded-xl border bg-card p-4 shadow-sm">
          <div className="text-sm text-muted-foreground">Resolution</div>
          <div className="mt-1 text-xl font-semibold">
            {market.resolved ? (market.outcome ? "YES" : "NO") : "Pending"}
          </div>
        </div>
      </div>

      <div className="mb-6 grid gap-4 lg:grid-cols-2">
        <div className="rounded-2xl border bg-card p-6 shadow-sm">
          <div className="text-xs uppercase tracking-[0.12em] text-muted-foreground">
            Resolution source
          </div>
          <div className="mt-2 text-base font-medium text-foreground">
            {market.resolutionSource || "—"}
          </div>
        </div>

        <div className="rounded-2xl border bg-card p-6 shadow-sm">
          <div className="text-xs uppercase tracking-[0.12em] text-muted-foreground">
            Creator
          </div>
          <div className="mt-2 text-base font-medium text-foreground">
            {shortAddress(market.creator)}
          </div>
        </div>
      </div>

      {market.resolved && (
        <div className="mb-6 grid gap-4 lg:grid-cols-2">
          <div className="rounded-2xl border bg-card p-6 shadow-sm">
            <div className="text-xs uppercase tracking-[0.12em] text-muted-foreground">
              Resolution evidence
            </div>
            <div className="mt-2 text-base font-medium text-foreground">
              {market.resolutionEvidenceUrl ? (
                <a
                  href={market.resolutionEvidenceUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="underline break-all"
                >
                  {market.resolutionEvidenceUrl}
                </a>
              ) : (
                "—"
              )}
            </div>
          </div>

          <div className="rounded-2xl border bg-card p-6 shadow-sm">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <div className="text-xs uppercase tracking-[0.12em] text-muted-foreground">
                  Resolved by
                </div>
                <div className="mt-2 text-base font-medium text-foreground">
                  {shortAddress(market.resolver)}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.12em] text-muted-foreground">
                  Resolved at
                </div>
                <div className="mt-2 text-base font-medium text-foreground">
                  {formatDateFromUnix(market.resolvedAt)}
                </div>
              </div>
            </div>
          </div>

          {market.resolutionNotes && (
            <div className="rounded-2xl border bg-card p-6 shadow-sm lg:col-span-2">
              <div className="text-xs uppercase tracking-[0.12em] text-muted-foreground">
                Resolution notes
              </div>
              <div className="mt-2 whitespace-pre-wrap text-base font-medium text-foreground">
                {market.resolutionNotes}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="mb-6">
        <ProbabilityChart history={history} />
      </div>

      <div className="grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
        <div className="space-y-6">
          <div className="rounded-2xl border bg-card p-6 shadow-sm">
            <h2 className="text-lg font-semibold">Market overview</h2>

            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div className="rounded-xl border p-4">
                <div className="text-sm text-muted-foreground">Question</div>
                <div className="mt-1 font-medium">{market.question || "—"}</div>
              </div>

              <div className="rounded-xl border p-4">
                <div className="text-sm text-muted-foreground">Resolution source</div>
                <div className="mt-1 font-medium">
                  {market.resolutionSource || "—"}
                </div>
              </div>

              <div className="rounded-xl border p-4">
                <div className="text-sm text-muted-foreground">Deadline</div>
                <div className="mt-1 font-medium">
                  {formatDateFromUnix(market.deadline)}
                </div>
              </div>

              <div className="rounded-xl border p-4">
                <div className="text-sm text-muted-foreground">Outcome</div>
                <div className="mt-1 font-medium">
                  {market.resolved ? (market.outcome ? "YES" : "NO") : "Pending"}
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border bg-card p-6 shadow-sm">
            <h2 className="text-lg font-semibold">AMM liquidity / prices</h2>

            <div className="mt-4 grid gap-4 sm:grid-cols-3">
              <div className="rounded-xl border p-4">
                <div className="text-sm text-muted-foreground">YES pool</div>
                <div className="mt-1 text-xl font-semibold">
                  {formatPol(market.yesPool)} POL
                </div>
              </div>
              <div className="rounded-xl border p-4">
                <div className="text-sm text-muted-foreground">NO pool</div>
                <div className="mt-1 text-xl font-semibold">
                  {formatPol(market.noPool)} POL
                </div>
              </div>
              <div className="rounded-xl border p-4">
                <div className="text-sm text-muted-foreground">Total pool</div>
                <div className="mt-1 text-xl font-semibold">
                  {formatPol(totalPool)} POL
                </div>
              </div>
            </div>

            <div className="mt-4 grid gap-4 sm:grid-cols-3">
              <div className="rounded-xl border p-4">
                <div className="text-sm text-muted-foreground">YES price</div>
                <div className="mt-1 text-xl font-semibold">
                  {formatPercentFromBps(yesPriceBps)}
                </div>
              </div>
              <div className="rounded-xl border p-4">
                <div className="text-sm text-muted-foreground">NO price</div>
                <div className="mt-1 text-xl font-semibold">
                  {formatPercentFromBps(noPriceBps)}
                </div>
              </div>
              <div className="rounded-xl border p-4">
                <div className="text-sm text-muted-foreground">Virtual liquidity</div>
                <div className="mt-1 text-xl font-semibold">
                  {formatPol(market.virtualLiquidity)} POL
                </div>
              </div>
            </div>
          </div>

          {isConnected && (
            <div className="rounded-2xl border bg-card p-6 shadow-sm">
              <h2 className="text-lg font-semibold">Your position</h2>

              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <div className="rounded-xl border p-4">
                  <div className="text-sm text-muted-foreground">YES shares</div>
                  <div className="mt-1 text-xl font-semibold">
                    {formatPol(userPosition.yesShares)}
                  </div>
                  <div className="mt-2 text-xs text-muted-foreground">
                    If YES wins: {formatPol(estimatedIfYesWins)} POL
                  </div>
                </div>

                <div className="rounded-xl border p-4">
                  <div className="text-sm text-muted-foreground">NO shares</div>
                  <div className="mt-1 text-xl font-semibold">
                    {formatPol(userPosition.noShares)}
                  </div>
                  <div className="mt-2 text-xs text-muted-foreground">
                    If NO wins: {formatPol(estimatedIfNoWins)} POL
                  </div>
                </div>
              </div>

              <div className="mt-4 grid gap-4 sm:grid-cols-3">
                <div className="rounded-xl border p-4">
                  <div className="text-sm text-muted-foreground">Claimable now</div>
                  <div className="mt-1 text-xl font-semibold">
                    {formatPol(claimablePayout)} POL
                  </div>
                </div>

                <div className="rounded-xl border p-4">
                  <div className="text-sm text-muted-foreground">YES side total shares</div>
                  <div className="mt-1 text-xl font-semibold">
                    {formatPol(market.totalYesShares)}
                  </div>
                </div>

                <div className="rounded-xl border p-4">
                  <div className="text-sm text-muted-foreground">NO side total shares</div>
                  <div className="mt-1 text-xl font-semibold">
                    {formatPol(market.totalNoShares)}
                  </div>
                </div>
              </div>

              {claimStatusText && (
                <div className="mt-4 rounded-xl border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
                  {claimStatusText}
                </div>
              )}

              <div className="mt-4">
                <button
                  type="button"
                  onClick={handleClaim}
                  disabled={!canClaim}
                  className="rounded-xl bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isClaimPending || isClaimConfirming
                    ? "Claiming..."
                    : userPosition.alreadyClaimed
                    ? "Already Claimed"
                    : market.resolved
                    ? "Claim Winnings"
                    : "Claim After Resolution"}
                </button>
              </div>
            </div>
          )}
        </div>

        <div>
          <TradePanel
            marketId={marketId}
            marketTitle={market.question}
            onTradeSuccess={async () => {
              await Promise.all([
                refetchMarket(),
                refetchPrices(),
                refetchUserPosition(),
              ]);
            }}
          />
        </div>
      </div>
    </div>
  );
}